/**
 * Street Autocomplete Helper
 *
 * Utilities for managing street autocomplete dropdown results:
 * selection, keyboard navigation, and DOM manipulation.
 */

/**
 * Navigate to sibling option in results list
 *
 * @param {Element} resultsContainer - The results container element
 * @param {boolean} isNext - true for next, false for previous
 * @returns {Element|null} The sibling element or fallback
 */
export function findSibling(resultsContainer, isNext) {
  const options = Array.from(
    resultsContainer.querySelectorAll('[role="option"]')
  )
  const selected = resultsContainer.querySelector('[aria-selected="true"]')

  if (!selected || options.length === 0) return null

  const index = options.indexOf(selected)
  const sibling = isNext ? options[index + 1] : options[index - 1]
  const fallback = isNext ? options[0] : options[options.length - 1]

  return sibling || fallback
}

/**
 * Select an option in the results list (visual only)
 */
export function selectOption(resultsContainer, inputTarget, target) {
  resultsContainer.querySelectorAll('[aria-selected="true"]').forEach(el => {
    el.removeAttribute('aria-selected')
    el.classList.remove('active')
  })

  target.setAttribute('aria-selected', 'true')
  target.classList.add('active')
  inputTarget.setAttribute('aria-activedescendant', target.id)
}

/**
 * Assign unique IDs to options that don't have one yet
 */
export function identifyOptions(resultsContainer) {
  let id = 0
  resultsContainer.querySelectorAll('[role="option"]:not([id])').forEach(el => {
    el.id = `${resultsContainer.id}-option-${id++}`
  })
}

/**
 * Hide and clear the results container
 */
export function hideResults(resultsContainer) {
  resultsContainer.hidden = true
  resultsContainer.innerHTML = ''
}

/**
 * Populate form fields from a selected autocomplete result
 *
 * @param {HTMLElement} selected - The selected option element
 * @returns {Object} Parsed field values
 */
export function parseSelection(selected) {
  const textValue = selected.textContent?.trim() || ''
  const addressParts = textValue ? textValue.split(',') : []

  return {
    street: addressParts[0] || '',
    streetnumber: selected.getAttribute('data-streetnumber') || '',
    zipcode: selected.getAttribute('data-zipcode') || '',
    neighborhood: selected.getAttribute('data-neighborhood') || '',
    city: selected.getAttribute('data-city') || '',
    uf: selected.getAttribute('data-uf') || '',
    latitude: selected.getAttribute('data-lat') || '',
    longitude: selected.getAttribute('data-lng') || '',
    hiddenValue: selected.getAttribute('data-street') || textValue
  }
}

/**
 * Build search URL with query params from base URL
 */
export function buildSearchUrl(baseUrl, query, stateUf, city) {
  const url = new URL(baseUrl, window.location.href)
  const params = new URLSearchParams(url.search.slice(1))

  params.append('q', query)

  if (stateUf && city) {
    params.append('state_uf', stateUf)
    params.append('city', city)
  }

  url.search = params.toString()
  return url.toString()
}

/**
 * Display fetch results in the results container
 */
export function displayResults(resultsContainer, html) {
  resultsContainer.innerHTML = html
  identifyOptions(resultsContainer)

  const hasResults = !!resultsContainer.querySelector('[role="option"]')
  resultsContainer.classList.add('show')
  resultsContainer.hidden = !hasResults
}
